import { useEffect, useRef } from "react";
import {
  Brain, Database, ShoppingCart, Zap, BarChart3, Cog,
  CircleDollarSign, Users, TrendingUp, ShoppingBag, Package, Warehouse,
  ClipboardCheck, Activity, Factory, FileJson, Image as ImageIcon, Layers, Network
} from "lucide-react";

export const Services = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll, .animate-on-scroll-scale');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Featured services (larger cards)
  const featuredServices = [
    {
      icon: Database,
      title: "ERP Solutions",
      description: "Comprehensive Enterprise Resource Planning implementation and optimization for complex business operations.",
      features: ["Implementation", "Customization", "Migration", "Support"],
      color: "amber"
    },
    {
      icon: Network,
      title: "System Integrations",
      description: "End-to-end integration architecture connecting all your business systems into a unified ecosystem.",
      features: ["API Development", "Data Sync", "Middleware", "Automation"],
      color: "cyan"
    },
    {
      icon: Brain,
      title: "AI & Automation",
      description: "Intelligent automation solutions powered by cutting-edge AI to transform your operations.",
      features: ["AI Agents", "RAG Systems", "Workflow Automation", "ML Models"],
      color: "violet"
    }
  ];

  // Standard services (smaller cards in grid)
  const services = [
    { icon: CircleDollarSign, title: "Financials", description: "GL/AP/AR automation, multi-currency, tax compliance", color: "amber" },
    { icon: Users, title: "CRM", description: "Salesforce, Dynamics 365, HubSpot integration", color: "blue" },
    { icon: TrendingUp, title: "Sales", description: "Pipeline management, forecasting, commission automation", color: "emerald" },
    { icon: ShoppingBag, title: "Procurement", description: "P2P automation, vendor portals, contract management", color: "orange" },
    { icon: Package, title: "Inventory", description: "Multi-location tracking, cycle counting, demand planning", color: "cyan" },
    { icon: Warehouse, title: "WMS", description: "Pick/pack/ship, barcode scanning, 3PL integration", color: "indigo" },
    { icon: ClipboardCheck, title: "QMS", description: "ISO compliance, audit trails, CAPA management", color: "teal" },
    { icon: Activity, title: "DDMRP", description: "Strategic buffers, flow optimization, lead time reduction", color: "yellow" },
    { icon: Factory, title: "Manufacturing", description: "Work orders, routing, assembly management", color: "slate" },
    { icon: FileJson, title: "EDI", description: "X12/EDIFACT, AS2 connectivity, automated workflows", color: "blue" },
    { icon: ImageIcon, title: "DAM", description: "Asset lifecycle, rights management, brand portals", color: "pink" },
    { icon: Layers, title: "Middleware", description: "Enterprise integration platforms and iPaaS solutions", color: "violet" },
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; border: string; glow: string }> = {
      amber: { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/20", glow: "group-hover:shadow-amber-500/10" },
      cyan: { bg: "bg-cyan-500/10", text: "text-cyan-400", border: "border-cyan-500/20", glow: "group-hover:shadow-cyan-500/10" },
      violet: { bg: "bg-violet-500/10", text: "text-violet-400", border: "border-violet-500/20", glow: "group-hover:shadow-violet-500/10" },
      blue: { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/20", glow: "group-hover:shadow-blue-500/10" },
      emerald: { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/20", glow: "group-hover:shadow-emerald-500/10" },
      orange: { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/20", glow: "group-hover:shadow-orange-500/10" },
      indigo: { bg: "bg-indigo-500/10", text: "text-indigo-400", border: "border-indigo-500/20", glow: "group-hover:shadow-indigo-500/10" },
      teal: { bg: "bg-teal-500/10", text: "text-teal-400", border: "border-teal-500/20", glow: "group-hover:shadow-teal-500/10" },
      yellow: { bg: "bg-yellow-500/10", text: "text-yellow-400", border: "border-yellow-500/20", glow: "group-hover:shadow-yellow-500/10" },
      slate: { bg: "bg-slate-500/10", text: "text-slate-400", border: "border-slate-500/20", glow: "group-hover:shadow-slate-500/10" },
      pink: { bg: "bg-pink-500/10", text: "text-pink-400", border: "border-pink-500/20", glow: "group-hover:shadow-pink-500/10" },
    };
    return colors[color] || colors.amber;
  };

  return (
    <section ref={sectionRef} id="services" className="py-32 relative overflow-hidden noise-overlay">
      {/* Background */}
      <div className="absolute inset-0 bg-zinc-950" />
      <div className="absolute inset-0 mesh-gradient-2 opacity-50" />
      <div className="absolute inset-0 dot-pattern opacity-30" />

      {/* Decorative Elements */}
      <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-20 left-0 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-[120px]" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-20">
          <div className="animate-on-scroll">
            <span className="label-mono text-amber-400 tracking-widest">
              Our Expertise
            </span>
          </div>

          <h2 className="mt-6 display-lg text-white animate-on-scroll stagger-1">
            Comprehensive
            <span className="text-gradient-gold"> Capabilities</span>
          </h2>

          <p className="mt-6 text-xl text-zinc-400 leading-relaxed animate-on-scroll stagger-2">
            End-to-end enterprise solutions engineered for scale, security, and seamless integration.
          </p>
        </div>

        {/* Featured Services - Large Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {featuredServices.map((service, index) => {
            const colors = getColorClasses(service.color);
            return (
              <div
                key={index}
                className={`animate-on-scroll-scale stagger-${index + 1} group relative card-featured p-8 hover-lift hover-glow`}
              >
                {/* Top gradient line */}
                <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${colors.text.replace('text-', 'via-')}/50 to-transparent`} />

                {/* Icon */}
                <div className={`w-14 h-14 rounded-2xl ${colors.bg} ${colors.border} border flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <service.icon className={`w-7 h-7 ${colors.text}`} />
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-gradient-gold transition-all duration-300">
                  {service.title}
                </h3>

                <p className="text-zinc-400 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-2">
                  {service.features.map((feature, featureIndex) => (
                    <span
                      key={featureIndex}
                      className={`px-3 py-1 text-xs font-medium rounded-full ${colors.bg} ${colors.text} ${colors.border} border`}
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Decorative corner */}
                <div className="absolute bottom-4 right-4 w-8 h-8 border-r border-b border-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            );
          })}
        </div>

        {/* Standard Services - Compact Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {services.map((service, index) => {
            const colors = getColorClasses(service.color);
            return (
              <div
                key={index}
                className={`animate-on-scroll stagger-${(index % 8) + 1} group card-editorial p-5 hover:border-amber-500/20`}
              >
                {/* Icon */}
                <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <service.icon className={`w-5 h-5 ${colors.text}`} />
                </div>

                {/* Title */}
                <h4 className="text-sm font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">
                  {service.title}
                </h4>

                {/* Description */}
                <p className="text-xs text-zinc-500 leading-relaxed line-clamp-2">
                  {service.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center animate-on-scroll">
          <div className="inline-flex items-center gap-4 glass-panel rounded-full px-6 py-3">
            <span className="text-sm text-zinc-400">Need a custom solution?</span>
            <a
              href="/contact"
              className="text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1 group"
            >
              Let's Talk
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
